import { useEffect, useRef, useState } from "react";
import { Camera, Upload, X, FileText, Image as ImageIcon, ChevronLeft, ChevronRight, Download } from "lucide-react";

async function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/**
 * PhotoUpload — accepts images (and optionally any file) and returns them as
 * base64 data-URLs. Perfect for talking to a backend that expects inline
 * attachments and dodges CORS/multipart headaches.
 *
 * value: [{ name, data, type }]  (or plain data-URL strings — normalised)
 */
export default function PhotoUpload({
  value = [],
  onChange,
  max = 6,
  maxSizeMB = 8,
  accept = "image/*",
  label = "Attach photos",
  testid = "photo-upload",
}) {
  const inputRef = useRef(null);
  const items = (value || []).map((v) =>
    typeof v === "string" ? { name: "photo", data: v, type: "image/*" } : v
  );

  const add = async (files) => {
    const next = [...items];
    for (const f of Array.from(files || []).slice(0, max - next.length)) {
      if (f.size > maxSizeMB * 1024 * 1024) continue;
      next.push({ name: f.name, type: f.type, data: await fileToDataUrl(f) });
    }
    onChange?.(next);
  };

  const remove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  const isImage = (it) => (it.type || "").startsWith("image/") || (it.data || "").startsWith("data:image");

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-black text-muted">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <div
            key={i}
            className="relative rounded-xl overflow-hidden group"
            style={{ background: "var(--surface)", border: "1px solid var(--border)", width: 96, height: 96 }}
          >
            {isImage(it) ? (
              <img src={it.data} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted">
                <FileText className="w-6 h-6" />
                <span className="text-[9px] truncate max-w-[80px]">{it.name}</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center bg-black/70 hover:bg-red-500 text-white transition"
              data-testid={`photo-remove-${i}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {items.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-24 h-24 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-primary-brand hover:text-primary-brand transition"
            style={{
              border: "1px dashed var(--border)",
              background: "var(--surface)",
              color: "var(--muted)",
            }}
            data-testid="photo-add"
          >
            {accept.includes("image") ? <Camera className="w-6 h-6" /> : <Upload className="w-6 h-6" />}
            <span className="text-[10px] uppercase tracking-widest font-black">Add</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(e) => add(e.target.files)}
      />
      <div className="text-[10px] text-muted uppercase tracking-widest">
        {items.length}/{max} · max {maxSizeMB}MB each
      </div>
    </div>
  );
}

/**
 * AttachmentGallery — displays inline photos + downloadable / viewable files
 * that came back from the backend (any of: photo_url, url, data-URL, base64,
 * or objects with {url, name, type}).
 */
export function AttachmentGallery({ items = [], testid = "attachment-gallery", pdfs = [] }) {
  const norm = (items || [])
    .map((it) => {
      if (!it) return null;
      if (typeof it === "string") return { url: it, name: "attachment" };
      return { url: it.url || it.data || it.photo_url || it.href, name: it.name || it.filename || "attachment", type: it.type };
    })
    .filter(Boolean);
  const allPdfs = [...pdfs, ...norm.filter((i) => /pdf/i.test(i.url || "") || /pdf/i.test(i.type || ""))];
  const images = norm.filter((i) => !allPdfs.includes(i) && (/(png|jpe?g|gif|webp|image\/)/i.test(i.url || i.type || "")));
  // Lightbox state — clicking a thumb opens the full image with keyboard +
  // arrow-button navigation between siblings. Critical for planners who need
  // to zoom into part-number labels, damage close-ups, etc.
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const open = (i) => setLightboxIdx(i);
  const close = () => setLightboxIdx(-1);
  const prev = () => setLightboxIdx((i) => (i > 0 ? i - 1 : images.length - 1));
  const next = () => setLightboxIdx((i) => (i < images.length - 1 ? i + 1 : 0));
  useEffect(() => {
    if (lightboxIdx < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // next/prev are closures over the latest render; the effect re-binds
    // whenever lightbox open state changes, which is what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIdx, images.length]);

  if (!images.length && !allPdfs.length) return null;
  const active = lightboxIdx >= 0 ? images[lightboxIdx] : null;
  return (
    <div className="space-y-3" data-testid={testid}>
      {images.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-muted mb-2 flex items-center gap-1">
            <ImageIcon className="w-3.5 h-3.5" /> Photos ({images.length}) · <span className="opacity-70">tap to enlarge</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((im, i) => (
              <button
                type="button"
                key={i}
                onClick={() => open(i)}
                className="rounded-xl overflow-hidden block hover:opacity-90 transition relative group"
                style={{ background: "var(--surface)", border: "1px solid var(--border)", aspectRatio: "1 / 1" }}
                data-testid={`${testid}-thumb-${i}`}
                title={im.name}
              >
                <img src={im.url} alt={im.name} className="w-full h-full object-cover" loading="lazy" />
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  style={{ background: "rgba(0,0,0,0.35)" }}
                >
                  <span className="pill !text-[10px] !py-1 !px-2" style={{ background: "rgba(0,0,0,0.7)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}>
                    <ImageIcon className="w-3 h-3" /> Enlarge
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {allPdfs.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-muted mb-2 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" /> Documents ({allPdfs.length})
          </div>
          <div className="space-y-2">
            {allPdfs.map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="panel-flat p-3 flex items-center gap-3 hover:border-primary-brand transition"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-sm truncate">{p.name || "Document"}</div>
                  <div className="text-[10px] text-muted uppercase tracking-widest truncate">{p.url}</div>
                </div>
                <span className="pill pill-orange">Open</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox overlay — Escape / arrow keys / on-screen chevrons all work.
          Backdrop click closes; image click does not. Download button pulls
          the raw asset. */}
      {active && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={active.name || "Photo"}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)" }}
          onClick={close}
          data-testid={`${testid}-lightbox`}
        >
          {/* Top bar: name + counter + download + close */}
          <div
            className="absolute top-0 left-0 right-0 flex items-center gap-3 px-4 py-3"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)" }}
          >
            <div className="text-white font-black text-sm truncate">{active.name || "Photo"}</div>
            <div className="text-white/60 text-[11px] uppercase tracking-widest tabular-nums shrink-0">
              {lightboxIdx + 1} / {images.length}
            </div>
            <div className="flex-1" />
            <a
              href={active.url}
              download={active.name || "photo"}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-ghost"
              onClick={(e) => e.stopPropagation()}
              data-testid={`${testid}-lightbox-download`}
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download</span>
            </a>
            <button
              className="btn btn-sm btn-ghost"
              onClick={close}
              data-testid={`${testid}-lightbox-close`}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Prev / Next chevrons */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center hover:scale-110 transition"
                style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff" }}
                aria-label="Previous photo"
                data-testid={`${testid}-lightbox-prev`}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center hover:scale-110 transition"
                style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff" }}
                aria-label="Next photo"
                data-testid={`${testid}-lightbox-next`}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* The image itself. object-contain so nothing gets cropped, click
              is stopped so backdrop-close doesn't fire when zooming/reading. */}
          <img
            src={active.url}
            alt={active.name || "Photo"}
            className="max-w-[92vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            style={{ boxShadow: "0 0 60px rgba(245,130,32,0.15)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testid}-lightbox-image`}
          />
        </div>
      )}
    </div>
  );
}
